/**
 * @file `Drawer` — a right-side, viewport-level overlay panel for the Emails
 *       sub-app. Hosts the scope-scaled review-send flow, the campaign
 *       "view email" preview, and per-email delivery detail.
 * @module @labcraft/admin/ui/emails/drawer
 *
 * Returns `null` when closed. While open it sits above app chrome via a
 * fixed backdrop (the same overlay approach as the ⌘K palette), closes on
 * Esc and backdrop click, and traps Tab focus inside the panel so keyboard
 * users can't wander into the dimmed page behind it — focus is restored to
 * the previously-focused element on close. Styling lives in `emails.css`
 * (`.drawer-*`, built on the `--op-shadow-drawer` token).
 */

import { useEffect, useRef, type JSX, type ReactNode } from 'react'

/** Selector for the elements a focus trap treats as tab stops. */
const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Props for {@link Drawer}.
 *
 * @property open - Whether the drawer is mounted/visible.
 * @property onClose - Dismiss the drawer (Esc, backdrop, close button).
 * @property title - The drawer heading (brand-font title row + aria-label).
 * @property kicker - Optional uppercase kicker rendered above the title.
 * @property children - The drawer body content.
 * @property foot - Optional footer content (actions) pinned to the bottom.
 * @property width - Optional explicit panel width (number → px, or CSS length).
 */
export type DrawerProps = {
  open: boolean
  onClose: () => void
  title: string
  kicker?: string
  children: ReactNode
  foot?: ReactNode
  width?: number | string
}

/**
 * Render the right-side drawer overlay, or `null` when closed.
 *
 * @param props - See {@link DrawerProps}.
 * @returns The drawer element, or `null` when `open` is false.
 */
export function Drawer(props: DrawerProps): JSX.Element | null {
  const { open, onClose, title, kicker, children, foot, width } = props
  const panelRef = useRef<HTMLElement | null>(null)

  // Esc-to-close + Tab focus-trap while open; restore focus on close.
  useEffect(() => {
    if (!open) return undefined
    const panel = panelRef.current
    const previouslyFocused = document.activeElement as HTMLElement | null

    const focusables = (): HTMLElement[] =>
      panel ? Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)) : []

    // Start keyboard nav inside the drawer (first control, else the panel).
    ;(focusables()[0] ?? panel)?.focus()

    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
        return
      }
      if (e.key !== 'Tab') return
      const items = focusables()
      const first = items[0]
      const last = items[items.length - 1]
      if (!first || !last) {
        e.preventDefault()
        panel?.focus()
        return
      }
      const active = document.activeElement
      if (e.shiftKey && active === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && active === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('keydown', onKey, true)
      previouslyFocused?.focus?.()
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <aside
        ref={panelRef}
        className="drawer-panel"
        style={width !== undefined ? { width } : undefined}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onClick={e => {
          e.stopPropagation()
        }}>
        <div className="drawer-head">
          <div>
            {kicker !== undefined && (
              <div className="drawer-head__k">{kicker}</div>
            )}
            <div className="drawer-head__title">{title}</div>
          </div>
          <button
            type="button"
            className="drawer-close"
            onClick={onClose}
            aria-label="Close">
            <svg
              width={15}
              height={15}
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              strokeLinecap="round"
              strokeLinejoin="round">
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </div>
        <div className="drawer-content">{children}</div>
        {foot !== undefined && <div className="drawer-foot">{foot}</div>}
      </aside>
    </div>
  )
}
